/* eslint-disable max-lines-per-function */
import {
  Button,
  Textarea,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Field,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@e2n/ui';
import { cn } from '@/lib/utils';
import { faComment, faThumbsUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useIsMobile, useProfile } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';
import { Field as TField } from '@/components/FeadbackToggle/types';
import { Rating } from '@/components/FeadbackToggle/Rating';

type FeedbackToggleProps = {
  title: string;
  fields: TField[];
};

export function FeedbackToggle({
  title,
  fields,
  className,
  ...others
}: FeedbackToggleProps & HTMLAttributes<HTMLDivElement>) {
  const [showToggle, setShowtoggle] = useState(true);
  const profile = useProfile();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const element = document.getElementById('body-container');
    if (element === undefined || element === null) {
      return;
    }
    const container = element;
    function onScroll() {
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (container.scrollTop > maxScroll - 10) {
        setShowtoggle(false);
      } else {
        setShowtoggle(true);
      }
    }

    element.addEventListener('scroll', onScroll);
    return () => element.removeEventListener('scroll', onScroll);
  }, []);

  function handleSubmit(formData: FormData) {
    if (!profile.manager) {
      return;
    }
    let message = '';
    const values = Object.fromEntries(formData.entries());
    Object.keys(values).forEach((k) => {
      message += `${k} \n ${values[k]}\n-------------------\n`;
    });
    Sentry.captureFeedback(
      {
        name: `${profile.manager.vorname} ${profile.manager.nachname}`,
        email: profile.manager.email,
        message: message,
      },
      {
        includeReplay: false,
        attachments: [],
      },
    );
    setOpen(false);
    toast({
      description: t('feedback.success'),
    });
  }

  if (!showToggle) {
    return null;
  }

  const triggerButton = (
    <div
      className={cn('absolute right-4 z-49 bottom-4', className)}
      {...others}>
      <Button className="rounded-full size-10 shadow-xl bg-primary-blue-600 group landscape:hidden lg:landscape:flex">
        <div className="relative w-6 h-6">
          <FontAwesomeIcon
            icon={faComment}
            className="text-white w-6 h-6 opacity-90"
          />
          <FontAwesomeIcon
            icon={faThumbsUp}
            className="w-3 h-3 absolute top-[0.025rem] -right-1 bg-primary-blue-600 group-hover:bg-primary-blue-700 transition-colors"
          />
        </div>
      </Button>
    </div>
  );

  const formContent = (
    <div className="w-full md:w-84">
      <div className="flex flex-col gap-6">
        <h1 className="text-xl font-semibold leading-7">{title}</h1>
        <form className="flex flex-col gap-4" action={handleSubmit}>
          {fields.map((field) => {
            if (field.kind === 'textarea') {
              return (
                <Field key={field.label}>
                  <FieldLabel>{field.label}</FieldLabel>
                  <Textarea
                    name={field.label}
                    placeholder={field.placeholder}
                  />
                </Field>
              );
            }

            if (field.kind === 'rating') {
              return (
                <Field key={field.label}>
                  <FieldLabel>{field.label}</FieldLabel>
                  <Rating
                    key={field.label}
                    name={field.label}
                    ratings={field.ratings}
                  />
                </Field>
              );
            }
          })}
          <Button type="submit">{t('feedback.abschicken')}</Button>
        </form>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className="sr-only">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{t('feedback.description')}</DrawerDescription>
          </DrawerHeader>
          <div className="p-4">{formContent}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent className="w-full">{formContent}</PopoverContent>
    </Popover>
  );
}
